import { Address } from 'viem'

export enum SwapperMode {
  EXACT_IN = 0,
  EXACT_OUT = 1,
  TARGET_DEBT = 2,
}

export interface EulerSwapQuoteParams {
  tokenIn?: Address
  tokenOut?: Address
  amount?: string // in base units
  vaultIn?: Address // source vault
  receiver?: Address // destination vault or user wallet
  /** EVC/sub-account that provides input (defaults to connected wallet if omitted) */
  accountIn?: Address
  /** EVC/sub-account that receives output (defaults to connected wallet if omitted) */
  accountOut?: Address
  /** EOA that originates the tx (defaults to connected wallet if omitted) */
  origin?: Address
  slippage?: number // in percent (0.1 = 0.1%)
  enabled?: boolean
  /**
   * Skip the final deposit of output token.
   * Set to true when receiver is a wallet (not a vault), e.g. for wallet or pooled market destinations.
   * This prevents the API from adding an invalid deposit(tokenOut, userWallet, ...) call.
   */
  skipSweepDepositOut?: boolean
}

/**
 * Extended params for repay/debt operations that use TARGET_DEBT mode
 */
export interface EulerSwapQuoteParamsExtended extends EulerSwapQuoteParams {
  /** Swap mode - EXACT_IN for regular swaps, TARGET_DEBT for repay operations */
  swapperMode?: SwapperMode
  /** Target debt amount for TARGET_DEBT mode */
  targetDebt?: string
  /** Current debt amount for TARGET_DEBT mode */
  currentDebt?: string
  /** Whether this is a repay operation */
  isRepay?: boolean
  /** Account to receive dust tokens */
  dustAccount?: Address
}

export interface EulerSwapTokenInfo {
  chainId: number
  address: Address
  name: string
  symbol: string
  decimals: number
  logoURI: string
}

export interface EulerSwapMulticallItem {
  functionName: string
  args: unknown[]
  data: `0x${string}`
}

export interface EulerSwapResponse {
  amountIn: string
  amountInMax: string
  amountOut: string
  amountOutMin: string
  vaultIn: Address
  receiver: Address
  accountIn: Address
  accountOut: Address
  tokenIn: EulerSwapTokenInfo
  tokenOut: EulerSwapTokenInfo
  slippage: number
  route: { providerName: string }[]
  swap: {
    swapperAddress: Address
    swapperData: `0x${string}`
    multicallItems: EulerSwapMulticallItem[]
  } | null
  verify: {
    verifierAddress: Address
    verifierData: `0x${string}`
    type: 'skimMin' | 'debtMax'
    vault: Address
    account: Address
    amount: string
    deadline: number
  } | null
}

/**
 * Narrowed version of EulerSwapResponse where swap and verify are guaranteed non-null.
 * Use with the `hasSwapAndVerify` type guard before accessing these fields.
 */
export type ValidatedEulerSwapResponse = EulerSwapResponse & {
  swap: NonNullable<EulerSwapResponse['swap']>
  verify: NonNullable<EulerSwapResponse['verify']>
}

/** Minimal shape accepted by {@link hasSwapAndVerify}. */
interface SwapQuoteLike {
  swap: unknown
  verify: unknown
}

/**
 * Type guard that checks whether a swap quote has non-null swap and verify data
 * with valid swap payload (non-empty swapperData and at least one multicall item).
 *
 * Works with both `EulerSwapResponse` (nullable fields) and `EulerSwapQuote`
 * (non-nullable fields) — the guard narrows the input type `T` to one where
 * `swap` and `verify` are guaranteed non-null.
 */
export function hasSwapAndVerify<T extends SwapQuoteLike>(
  quote: T | null | undefined,
): quote is T & { swap: NonNullable<T['swap']>; verify: NonNullable<T['verify']> } {
  if (quote == null || quote.swap == null || quote.verify == null) return false

  const swap = quote.swap as Record<string, unknown>
  if (!swap.swapperData || swap.swapperData === '0x') return false

  const multicallItems = swap.multicallItems
  if (!Array.isArray(multicallItems) || multicallItems.length === 0) return false

  return true
}

export interface EulerSwapApiResponse {
  success: boolean
  data: EulerSwapResponse[]
  statusCode: number
  message?: string
}

export interface UseEulerSwapQuoteResult {
  /** Best quote (first in sorted order). Null when unavailable. */
  quote: EulerSwapResponse | null
  /** All valid quotes sorted by preference (best first). */
  quotes: EulerSwapResponse[]
  isLoading: boolean
  isRefetching: boolean
  error: Error | null
  /** Manually refetch quotes from the API. */
  refetch: () => void
}
